import React, {
  lazy, memo, Suspense, useCallback, useEffect, useMemo, useState,
} from 'react'

import {
  Badge,
  Icon,
  Button,
  ChevronDown,
  MoreFilters as MoreFiltersIcon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import { POPOVER_FEATURES } from 'Pods/SearchPage/Shared/FeaturesContent/constants'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import { clearAllFilters } from 'Pods/SearchPage/Store/actions/clearAllFilters'
import { clearDatePublished } from 'Pods/SearchPage/Store/actions/datePublished'
import { clearDesigners } from 'Pods/SearchPage/Store/actions/designers'
import { addFileType, removeFileType } from 'Pods/SearchPage/Store/actions/fileTypes'
import { clearLicenses } from 'Pods/SearchPage/Store/actions/licenses'
import { clearMoreFilters } from 'Pods/SearchPage/Store/actions/moreFilters'
import { clearQualityContent } from 'Pods/SearchPage/Store/actions/qualityContent'
import {
  ADVANCED_FILTER, ASSET_TYPE, DATE_PUBLISHED, DEFAULT_STATE, LICENSE_TYPE, QUALITY_LEVEL,
} from 'Pods/SearchPage/Store/constants'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'
import { getFiltersCount, getSecondaryFiltersCount } from 'Pods/SearchPage/Store/selectors'
import FiltersWrapper from 'Pods/Shared/FiltersWrapper/FiltersWrapper'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'
import useBreakpoint from 'Utils/useBreakpoint'
import useMediaQuery from 'Utils/useMediaQuery'

import Columns from './Components/Columns/Columns'

// Keeps the drawer (and its CollapsibleSection subtree) out of the initial search pack. Safe to
// suspend: it renders only after the compact-breakpoint trigger is clicked, so this boundary is
// never reached during the page's SSR pass. Mirrors the CustomRange split in DatePublishedContent.
const MoreFiltersDrawer = lazy(() => import(
  /* webpackChunkName: "search-more-filters-drawer" */ 'Pods/LazyLoaded/MoreFiltersDrawer/MoreFiltersDrawer'
))

const prefixIcon = <Icon notSpaced svgComponent={MoreFiltersIcon} color="dark" size="small" />

const trailIcon = (
  <Icon
    notSpaced
    svgComponent={ChevronDown}
    color="darker"
    size="small"
    className="more-filters__trigger-chevron"
  />
)

// Below lg row 1 has no dropdowns (Figma 415:23304, 415:23612), so every filter opens in the
// All Filters drawer, a side sheet, behind a "Filters" button.
const COMPACT_BREAKPOINTS = ['xs', 'sm', 'md']
// Mirror $main-filters-breakpoints in search_page.scss: below it the Quality dropdown is hidden from
// the bar. Not a useBreakpoint token.
const QUALITY_OVERFLOW_QUERY = '(max-width: 1439.98px)'
// Mirror main-filters__slot--xl in search_page.scss: from xl Designer has its own row 1 dropdown.
const DESIGNER_IN_BAR_BREAKPOINTS = ['xl', 'xxl']
const DEFAULT_LICENSES = [LICENSE_TYPE.ALL]
const DEFAULT_QUALITY = [QUALITY_LEVEL.ANY]

function MoreFilters() {
  const translate = useTranslate()

  const advancedFilters = useSearchPageStore(state => state.advancedFilters)
  const hasCgtStandard = advancedFilters.includes(ADVANCED_FILTER.CGT_STANDARD_ONLY)
  const personalFilters = useSearchPageStore(state => state.personalFilters ?? EMPTY_ARRAY)
  const purchaseFilter = useSearchPageStore(state => state.purchaseFilter)
  const fileTypes = useSearchPageStore(state => state.fileTypes ?? EMPTY_ARRAY)
  const nativeOnly = useSearchPageStore(state => state.nativeOnly)
  const onSale = useSearchPageStore(state => state.onSale)
  const withSubscription = useSearchPageStore(state => state.withSubscription)
  const priceRanges = useSearchPageStore(state => state.priceRanges ?? EMPTY_ARRAY)
  const minPrice = useSearchPageStore(state => state.minPrice ?? '')
  const maxPrice = useSearchPageStore(state => state.maxPrice ?? '')
  const licenses = useSearchPageStore(state => state.licenses ?? DEFAULT_LICENSES)
  const datePublished = useSearchPageStore(state => state.datePublished ?? DATE_PUBLISHED.ALL_TIME)
  const quality = useSearchPageStore(state => state.quality ?? DEFAULT_QUALITY)
  const contentSource = useSearchPageStore(state => state.contentSource ?? DEFAULT_STATE.contentSource)
  const designers = useSearchPageStore(state => state.designers ?? EMPTY_ARRAY)
  const designerScopes = useSearchPageStore(state => state.designerScopes ?? EMPTY_ARRAY)
  const hasSecondaryFilters = useSearchPageStore(state => getSecondaryFiltersCount(state) > 0)
  const hasTags = useSearchPageStore(state => (state.tags ?? EMPTY_ARRAY).length > 0)
  const is3DOnly = useSearchPageStore(state => state.assetType === ASSET_TYPE.THREE_D_ONLY)
  const popoverFeaturesCount = useSearchPageStore(
    state => POPOVER_FEATURES.filter(feature => feature.select(state)).length,
  )
  // Below md the Clear button is hidden, so the compact trigger carries the count as a badge.
  const activeFiltersCount = useSearchPageStore(state => getFiltersCount(state))
  const breakpoint = useBreakpoint()
  // Gate the popover/modal split until mounted so the first client render matches SSR
  // (useBreakpoint is undefined on the server), avoiding a hydration mismatch.
  const [hasMounted, setHasMounted] = useState(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [triggerCloseDropdown, setTriggerCloseDropdown] = useState(false)

  useEffect(() => {
    setHasMounted(true)
  }, [])

  const isCompact = hasMounted && COMPACT_BREAKPOINTS.includes(breakpoint)
  const isDesktop = hasMounted && !isCompact
  // At md Price has its own row 1 dropdown (main-filters__slot--md), so the drawer leaves it out.
  const isPriceInBar = hasMounted && breakpoint === 'md'
  // Quality's row 1 slot is hidden below 1440 (main-filters__slot--wide in search_page.scss), so it
  // overflows into this popover to stay reachable. License and Date published have no row 1 slot
  // (Figma 415:19987), so they are always here; Designer is here below xl only.
  const isBelowQualityBreakpoint = useMediaQuery(QUALITY_OVERFLOW_QUERY)
  const showQualityOverflow = isDesktop && isBelowQualityBreakpoint
  const showPopoverSections = isDesktop
  const showDesignerSection = showPopoverSections && !DESIGNER_IN_BAR_BREAKPOINTS.includes(breakpoint)

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false)
  }, [])

  useEffect(() => {
    if (!isCompact && isDrawerOpen) {
      setIsDrawerOpen(false)
    }
  }, [isCompact, isDrawerOpen])

  // Filters owned by the More Filters popover itself; drives the desktop trigger and its Reset.
  // CGT Standard shares advancedFilters but is rendered in the Quality group.
  const hasOwnFilters = useMemo(() => {
    return (
      advancedFilters.some(filter => filter !== ADVANCED_FILTER.CGT_STANDARD_ONLY) ||
      personalFilters.length > 0 ||
      purchaseFilter != null
    )
  }, [advancedFilters, personalFilters, purchaseFilter])

  // On compact breakpoints the trigger opens the All Filters modal, which also hosts
  // File Format, Price and Date Published — those count toward the trigger's active state there only.
  const hasModalFilters = useMemo(() => {
    const hasPriceFilters = onSale ||
      withSubscription ||
      priceRanges.length > 0 ||
      minPrice !== '' ||
      maxPrice !== ''

    return (
      hasOwnFilters ||
      fileTypes.length > 0 ||
      (!isPriceInBar && hasPriceFilters) ||
      licenses[0] !== LICENSE_TYPE.ALL ||
      quality[0] !== QUALITY_LEVEL.ANY ||
      contentSource !== DEFAULT_STATE.contentSource ||
      hasCgtStandard ||
      datePublished !== DATE_PUBLISHED.ALL_TIME ||
      designers.length > 0 ||
      designerScopes.length > 0 ||
      hasTags ||
      hasSecondaryFilters
    )
  }, [
      hasOwnFilters,
      fileTypes,
      isPriceInBar,
      onSale,
      withSubscription,
      priceRanges,
      minPrice,
      maxPrice,
      licenses,
      quality,
      contentSource,
      hasCgtStandard,
      datePublished,
      designers,
      designerScopes,
      hasTags,
      hasSecondaryFilters,
    ])

  // "+n" on the desktop trigger (Figma 2989:109667): every value that is on inside the popover at
  // this width, counted the way "Clear (n)" counts them (getFiltersCount). Filters from hidden row 1
  // dropdowns count only while they overflow into the popover.
  const popoverFiltersCount = useMemo(() => {
    let count = advancedFilters.filter(filter => filter !== ADVANCED_FILTER.CGT_STANDARD_ONLY).length
      + personalFilters.length
      + (purchaseFilter != null ? 1 : 0)

    if (is3DOnly) count += popoverFeaturesCount
    if (showQualityOverflow) {
      count += quality.filter(level => level !== QUALITY_LEVEL.ANY).length
        + (contentSource !== DEFAULT_STATE.contentSource ? 1 : 0)
        + (hasCgtStandard ? 1 : 0)
    }
    if (showPopoverSections) {
      count += licenses.filter(license => license !== LICENSE_TYPE.ALL).length
        + (datePublished !== DATE_PUBLISHED.ALL_TIME ? 1 : 0)
    }
    if (showDesignerSection) {
      count += designers.length + designerScopes.length
    }

    return count
  }, [
    advancedFilters,
    personalFilters,
    purchaseFilter,
    is3DOnly,
    popoverFeaturesCount,
    showQualityOverflow,
    quality,
    contentSource,
    hasCgtStandard,
    showPopoverSections,
    licenses,
    datePublished,
    showDesignerSection,
    designers,
    designerScopes,
  ])

  const hasPopoverFilters = popoverFiltersCount > 0

  // "+n" on the trigger at md (Figma 3231:151408): every value that is on inside the drawer's own
  // sections there. Price and the row 2 filters (features, style, poly count, tags) sit outside the
  // drawer at md, so they do not count, even where the drawer repeats them.
  const drawerFiltersCount = useMemo(() => (
    advancedFilters.filter(filter => filter !== ADVANCED_FILTER.CGT_STANDARD_ONLY).length
      + personalFilters.length
      + (purchaseFilter != null ? 1 : 0)
      + fileTypes.length
      + (nativeOnly ? 1 : 0)
      + quality.filter(level => level !== QUALITY_LEVEL.ANY).length
      + (contentSource !== DEFAULT_STATE.contentSource ? 1 : 0)
      + (hasCgtStandard ? 1 : 0)
      + licenses.filter(license => license !== LICENSE_TYPE.ALL).length
      + (datePublished !== DATE_PUBLISHED.ALL_TIME ? 1 : 0)
      + designers.length
      + designerScopes.length
  ), [
    advancedFilters,
    personalFilters,
    purchaseFilter,
    fileTypes,
    nativeOnly,
    quality,
    contentSource,
    hasCgtStandard,
    licenses,
    datePublished,
    designers,
    designerScopes,
  ])

  const handleReset = useCallback(() => {
    clearMoreFilters()
    if (is3DOnly) {
      POPOVER_FEATURES.forEach(feature => feature.onChange(false))
    }
    if (showQualityOverflow) {
      clearQualityContent()
    }
    if (showPopoverSections) {
      clearLicenses()
      clearDatePublished()
    }
    if (showDesignerSection) {
      clearDesigners()
    }
  }, [is3DOnly, showQualityOverflow, showPopoverSections, showDesignerSection])

  const handleFileTypeChange = useCallback((_, value, isOn) => {
    if (isOn) {
      addFileType(value)
    } else {
      removeFileType(value)
    }
  }, [])

  const handleOpenDrawer = useCallback(() => {
    setIsDrawerOpen(true)
  }, [])

  const handleDateRangeApply = useCallback(() => {
    setTriggerCloseDropdown(true)
  }, [])

  const content = useMemo(() => (
    <>
      <Columns
        showQuality={showQualityOverflow}
        showDesigner={showDesignerSection}
        onDateRangeApply={handleDateRangeApply}
      />
      <div className="more-filters__footer">
        <Button
          type="ghost"
          size="tiny"
          customClass="filter-wrapper__reset-button"
          onClick={handleReset}
          disabled={!hasPopoverFilters}
        >
          {translate('search_page.filters.more_filters.reset')}
        </Button>
      </div>
    </>
  ), [handleDateRangeApply, handleReset, hasPopoverFilters, showQualityOverflow, showDesignerSection, translate])

  const hasActiveFilters = activeFiltersCount > 0
  // Figma 3231:151408 labels the trigger "More filters" with a "+n" at md, and Figma 415:23612
  // "Filters" below it.
  const triggerLabel = translate('search_page.filters.more_filters.trigger_label')
  const triggerLabelWithCount = translate(
    'search_page.filters.more_filters.trigger_label_with_count', { count: activeFiltersCount },
  )
  const moreFiltersLabel = translate('search_page.filters.more_filters.desktop_title')
  const moreFiltersLabelWithCount = translate(
    'search_page.filters.more_filters.desktop_title_with_count', { count: drawerFiltersCount },
  )
  const hasDrawerFilters = drawerFiltersCount > 0
  let triggerAriaLabel = hasActiveFilters ? triggerLabelWithCount : triggerLabel
  if (isPriceInBar) {
    triggerAriaLabel = hasDrawerFilters ? moreFiltersLabelWithCount : moreFiltersLabel
  }

  // The breakpoint is unknown until mount, so the server renders both triggers and CSS shows the
  // one for the viewport (more-filters__trigger / more-filters__desktop in search_page.scss).
  // Swapping one for the other on hydration reflowed the mobile filter row and shifted the grid.
  const showCompactTrigger = !hasMounted || isCompact
  const showDesktopTrigger = !hasMounted || isDesktop

  return (
    <>
      {showCompactTrigger && (
        <Button
          type="secondary"
          variant="with-icon"
          icon={prefixIcon}
          isActive={isPriceInBar ? hasDrawerFilters : hasModalFilters}
          onClick={handleOpenDrawer}
          customClass="filter-wrapper__button more-filters__trigger"
          aria-label={triggerAriaLabel}
          aria-expanded={isDrawerOpen}
          trailIcon={trailIcon}
        >
          {/* Both labels render on the server and CSS picks one by width, so hydration never resizes
              the trigger (the breakpoint is unknown until mount). */}
          <span className="more-filters__trigger-label more-filters__trigger-label--compact">
            {triggerLabel}
          </span>
          <span className="more-filters__trigger-label more-filters__trigger-label--wide">
            {moreFiltersLabel}
            {hasDrawerFilters && (
              <span className="filter-wrapper__more-count cgt-caption-semibold cgt-caption-semibold--dark-bg">
                {`+${drawerFiltersCount}`}
              </span>
            )}
          </span>
          {hasActiveFilters && (
            <Badge type="primary" className="more-filters__trigger-badge">
              {activeFiltersCount}
            </Badge>
          )}
        </Button>
      )}
      {showDesktopTrigger && (
        <FiltersWrapper
          trailIconSize="small"
          placeholder={translate('search_page.filters.more_filters.desktop_title')}
          prefixIcon={prefixIcon}
          content={content}
          hasSelectedAnyFilters={hasPopoverFilters}
          selectedCount={popoverFiltersCount}
          triggerCloseDropdown={triggerCloseDropdown}
          setTriggerCloseDropdown={setTriggerCloseDropdown}
          positionHorizontal="left"
          closingDropdownClassName="filter-wrapper main-filters__min-width-dropdown more-filters__desktop"
          contentClassName="more-filters__popover-content more-filters__popover-content--columns"
        />
      )}
      {isCompact && isDrawerOpen && (
        <Suspense fallback={null}>
          <MoreFiltersDrawer
            onClose={handleCloseDrawer}
            onReset={clearAllFilters}
            showPrice={!isPriceInBar}
            fileTypes={fileTypes}
            onFileTypeChange={handleFileTypeChange}
          />
        </Suspense>
      )}
    </>
  )
}

export default memo(MoreFilters)
